import {
  signIn as amplifySignIn,
  signUp as amplifySignUp,
  signOut as amplifySignOut,
  confirmSignUp as amplifyConfirmSignUp,
  confirmSignIn,
  fetchAuthSession,
  fetchUserAttributes,
  updatePassword,
  updateUserAttributes as amplifyUpdateUserAttributes,
  confirmUserAttribute,
  resetPassword,
  confirmResetPassword,
  resendSignUpCode,
} from 'aws-amplify/auth';

// Adapter: returns { idToken: { jwtToken }, accessToken: { jwtToken } }
export const getCurrentSession = async () => {
  const session = await fetchAuthSession();
  return {
    idToken: { jwtToken: session.tokens?.idToken?.toString() },
    accessToken: { jwtToken: session.tokens?.accessToken?.toString() },
  };
};

// Adapter: returns { attributes: { sub, ... }, signInUserSession: { accessToken: { jwtToken } } }
export const getCurrentAuthenticatedUser = async () => {
  const [session, attributes] = await Promise.all([fetchAuthSession(), fetchUserAttributes()]);
  return {
    attributes,
    signInUserSession: {
      accessToken: { jwtToken: session.tokens?.accessToken?.toString() },
    },
  };
};

// Adapter: returns { identityId }
export const getCurrentCredentials = async () => {
  const session = await fetchAuthSession();
  return { identityId: session.identityId };
};

export const signOut = () => amplifySignOut();

// Adapter: returns { challengeName } when NEW_PASSWORD_REQUIRED
export const signIn = async (email: string, password: string) => {
  const result = await amplifySignIn({ username: email, password });
  if (result.nextStep?.signInStep === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED') {
    return { challengeName: 'NEW_PASSWORD_REQUIRED' };
  }
  return result;
};

// Adapter: returns { userSub } (v6 returns { userId })
export const signUp = async (params: {
  username: string;
  password: string;
  attributes: Record<string, string>;
}) => {
  const result = await amplifySignUp({
    username: params.username,
    password: params.password,
    options: { userAttributes: params.attributes },
  });
  return { userSub: result.userId };
};

export const confirmSignUp = (email: string, confirmationCode: string) =>
  amplifyConfirmSignUp({ username: email, confirmationCode });

// Adapter: first arg (user) ignored in v6
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const changePassword = (_user: any, oldPassword: string, newPassword: string) =>
  updatePassword({ oldPassword, newPassword });

// Adapter: first arg (user) ignored in v6
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const updateUserAttributes = (_user: any, params: Record<string, string>) =>
  amplifyUpdateUserAttributes({ userAttributes: params });

export const verifyCurrentUserAttributeSubmit = (type: string, confirmationCode: string) =>
  confirmUserAttribute({ userAttributeKey: type as 'email' | 'phone_number', confirmationCode });

export const forgotPassword = (email: string) => resetPassword({ username: email });

export const forgotPasswordSubmit = (email: string, code: string, newPassword: string) =>
  confirmResetPassword({ username: email, confirmationCode: code, newPassword });

// Adapter: first arg (user) ignored in v6
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const completeNewPassword = (_user: any, newPassword: string) =>
  confirmSignIn({ challengeResponse: newPassword });

export const resendVerificationCode = (userName: string) => resendSignUpCode({ username: userName });
